/**
 * Política única de idioma (Worker + geo + preferencia + URL).
 *
 * Orden i18next (src/i18n/config.ts):
 *   ?lang= → usesSpanishDefaultForSite() → español por defecto → preferencia explícita (solo internacional)
 *   → idioma edge (cookie / __TEMPLATE_CONFIG__) → último locale en user_language → navegador.
 */

import { markI18nUserLanguageChoice } from "@/lib/i18nEdgeSync";
import type { SupportedLanguage } from "@/lib/languages";
import { normalizeLanguageCode } from "@/lib/languages";
import { usesSpanishDefaultForSite } from "@/lib/siteGeoPolicy";

export const USER_LANGUAGE_STORAGE_KEY = "user_language";

export function readPersistedUserLanguage(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(USER_LANGUAGE_STORAGE_KEY)?.trim();
    return raw ? raw.replace(/_/g, "-") : null;
  } catch {
    return null;
  }
}

export function writePersistedUserLanguage(bcp47: string): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(USER_LANGUAGE_STORAGE_KEY, bcp47);
  } catch {
    /* ignore */
  }
}

/** Valor crudo de ?lang= (sin mutar la URL). */
export function readLangQueryParam(): string | null {
  if (typeof window === "undefined") return null;
  const v = new URLSearchParams(window.location.search).get("lang")?.trim();
  return v || null;
}

/**
 * Resuelve ?lang= al código i18n (languageOnly). En AR, cualquier valor distinto de español
 * se ignora aquí para que spanishDefaultGeo (i18n) fije español.
 */
export function resolveQueryLangForDetection(raw: string): SupportedLanguage | undefined {
  const base = normalizeLanguageCode(raw);
  if (usesSpanishDefaultForSite() && base !== "es") {
    return undefined;
  }
  return base;
}

/** Selector / hooks: una sola entrada para preferencia explícita. */
export function applyExplicitUserLanguage(
  i18n: { changeLanguage: (lng: string) => Promise<unknown> },
  lng: SupportedLanguage,
): void {
  markI18nUserLanguageChoice();
  void i18n.changeLanguage(lng);
}

/**
 * Tras ?lang= en la URL: persiste intención solo cuando corresponde (no marcar “elegí inglés” en AR si el link traía lang=en).
 */
export function markChoiceAfterLangQuery(raw: string): void {
  if (!usesSpanishDefaultForSite()) {
    markI18nUserLanguageChoice();
    return;
  }
  if (normalizeLanguageCode(raw) === "es") {
    markI18nUserLanguageChoice();
  }
}
